// First Created - 04 Sep 2026
// Author - Atul Pandey
// Purpose - RTK Query API slice for Trinity Intention Board candidates.

import {
  createSelector,
  createEntityAdapter,
} from '@reduxjs/toolkit'

import {
  apiSlice,
} from '@/app/api/apiSlice'

/*
|--------------------------------------------------------------------------
| Entity Adapter
|--------------------------------------------------------------------------
*/

const orgIntentionCandidatesAdapter =
  createEntityAdapter({})

const initialState =
  orgIntentionCandidatesAdapter
    .getInitialState()

/*
|--------------------------------------------------------------------------
| API Slice
|--------------------------------------------------------------------------
*/

export const orgIntentionCandidatesApiSlice =
  apiSlice.injectEndpoints({

    endpoints:
      builder => ({

        /*
        |--------------------------------------------------------------------------
        | Get Intention Candidates
        |--------------------------------------------------------------------------
        */

        getOrgIntentionCandidates:
          builder.query({

            query:
              ({
                intentionBoardId,
                candidateStatus,
              }) => ({

                url:
                  '/orgIntentionCandidate/getOrgIntentionCandidates',

                params: {

                  intentionBoardId,

                  ...(candidateStatus
                    ? {
                        candidateStatus,
                      }
                    : {}),

                },

                validateStatus:
                  (
                    response,
                    result
                  ) => {

                    return (
                      response.status ===
                        200 &&
                      !result.isError
                    )

                  },

              }),

            transformResponse:
              responseData => {

                const loadedOrgIntentionCandidates =
                  responseData.map(
                    intentionCandidate => {

                      intentionCandidate.id =
                        intentionCandidate._id

                      return intentionCandidate

                    }
                  )

                return orgIntentionCandidatesAdapter
                  .setAll(
                    initialState,
                    loadedOrgIntentionCandidates
                  )

              },

            providesTags:
              (
                result,
                error,
                arg
              ) => {

                if (
                  result?.ids
                ) {

                  return [

                    {
                      type:
                        'OrgIntentionCandidate',

                      id:
                        `LIST-${arg?.intentionBoardId}`,
                    },

                    ...result.ids.map(
                      id => ({

                        type:
                          'OrgIntentionCandidate',

                        id,

                      })
                    ),

                  ]

                }

                return [

                  {
                    type:
                      'OrgIntentionCandidate',

                    id:
                      `LIST-${arg?.intentionBoardId}`,
                  },

                ]

              },

          }),

        /*
        |--------------------------------------------------------------------------
        | Get Intention Candidate By Id
        |--------------------------------------------------------------------------
        */

        getOrgIntentionCandidateById:
          builder.query({

            query:
              ({
                intentionCandidateId,
              }) => ({

                url:
                  '/orgIntentionCandidate/getOrgIntentionCandidateById',

                params: {
                  intentionCandidateId,
                },

                validateStatus:
                  (
                    response,
                    result
                  ) => {

                    return (
                      response.status ===
                        200 &&
                      !result.isError
                    )

                  },

              }),

            providesTags:
              (
                result,
                error,
                arg
              ) => [

                {
                  type:
                    'OrgIntentionCandidate',

                  id:
                    arg
                      ?.intentionCandidateId,
                },

              ],

          }),

        /*
        |--------------------------------------------------------------------------
        | Create Intention Candidate
        |--------------------------------------------------------------------------
        */

        createNewOrgIntentionCandidate:
          builder.mutation({

            query:
              initialOrgIntentionCandidate => ({

                url:
                  '/orgIntentionCandidate/createNewOrgIntentionCandidate',

                method:
                  'POST',

                body: {
                  ...initialOrgIntentionCandidate,
                },

              }),

            invalidatesTags:
              (
                result,
                error,
                arg
              ) => [

                {
                  type:
                    'OrgIntentionCandidate',

                  id:
                    `LIST-${arg?.intentionBoardId}`,
                },

              ],

          }),

        /*
        |--------------------------------------------------------------------------
        | Update Intention Candidate
        |--------------------------------------------------------------------------
        */

        updateOrgIntentionCandidate:
          builder.mutation({

            query:
              initialOrgIntentionCandidate => ({

                url:
                  '/orgIntentionCandidate/updateOrgIntentionCandidate',

                method:
                  'PATCH',

                body: {
                  ...initialOrgIntentionCandidate,
                },

              }),

            invalidatesTags:
              (
                result,
                error,
                arg
              ) => [

                {
                  type:
                    'OrgIntentionCandidate',

                  id:
                    arg
                      ?.intentionCandidateId,
                },

                {
                  type:
                    'OrgIntentionCandidate',

                  id:
                    `LIST-${arg?.intentionBoardId}`,
                },

              ],

          }),

        /*
        |--------------------------------------------------------------------------
        | Mark Candidate Invited
        |--------------------------------------------------------------------------
        */

        markOrgIntentionCandidateInvited:
          builder.mutation({

            query:
              ({
                intentionCandidateId,
              }) => ({

                url:
                  '/orgIntentionCandidate/markOrgIntentionCandidateInvited',

                method:
                  'PATCH',

                body: {
                  intentionCandidateId,
                },

              }),

            invalidatesTags:
              (
                result,
                error,
                arg
              ) => [

                {
                  type:
                    'OrgIntentionCandidate',

                  id:
                    arg
                      ?.intentionCandidateId,
                },

                {
                  type:
                    'OrgIntentionCandidate',

                  id:
                    `LIST-${arg?.intentionBoardId}`,
                },

              ],

          }),

        /*
        |--------------------------------------------------------------------------
        | Delete Intention Candidate
        |--------------------------------------------------------------------------
        */

        deleteOrgIntentionCandidate:
          builder.mutation({

            query:
              ({
                intentionCandidateId,
              }) => ({

                url:
                  '/orgIntentionCandidate/deleteOrgIntentionCandidate',

                method:
                  'DELETE',

                body: {
                  intentionCandidateId,
                },

              }),

            invalidatesTags:
              (
                result,
                error,
                arg
              ) => [

                {
                  type:
                    'OrgIntentionCandidate',

                  id:
                    arg
                      ?.intentionCandidateId,
                },

                {
                  type:
                    'OrgIntentionCandidate',

                  id:
                    `LIST-${arg?.intentionBoardId}`,
                },

              ],

          }),

      }),

  })

/*
|--------------------------------------------------------------------------
| Hooks
|--------------------------------------------------------------------------
*/

export const {

  useGetOrgIntentionCandidatesQuery,

  useGetOrgIntentionCandidateByIdQuery,

  useCreateNewOrgIntentionCandidateMutation,

  useUpdateOrgIntentionCandidateMutation,

  useMarkOrgIntentionCandidateInvitedMutation,

  useDeleteOrgIntentionCandidateMutation,

} =
  orgIntentionCandidatesApiSlice

/*
|--------------------------------------------------------------------------
| Query Result Selector
|--------------------------------------------------------------------------
*/

export const selectOrgIntentionCandidatesResult =
  (
    intentionBoardId,
    candidateStatus
  ) =>
    orgIntentionCandidatesApiSlice
      .endpoints
      .getOrgIntentionCandidates
      .select({

        intentionBoardId,

        ...(candidateStatus
          ? {
              candidateStatus,
            }
          : {}),

      })

/*
|--------------------------------------------------------------------------
| Candidate Data Selector
|--------------------------------------------------------------------------
*/

export const selectOrgIntentionCandidatesData =
  (
    intentionBoardId,
    candidateStatus
  ) =>
    createSelector(

      selectOrgIntentionCandidatesResult(
        intentionBoardId,
        candidateStatus
      ),

      orgIntentionCandidatesResult =>
        orgIntentionCandidatesResult
          .data

    )

/*
|--------------------------------------------------------------------------
| Entity Selectors
|--------------------------------------------------------------------------
*/

export const selectOrgIntentionCandidateSelectors =
  (
    intentionBoardId,
    candidateStatus
  ) =>
    orgIntentionCandidatesAdapter
      .getSelectors(

        state => {

          const candidatesData =
            selectOrgIntentionCandidatesData(
              intentionBoardId,
              candidateStatus
            )(
              state
            )

          return (
            candidatesData ??
            initialState
          )

        }

      )